import { useEffect, useRef } from 'react';

/**
 * Plays low-volume, looping white noise whenever `enabled` is true.
 * Returns nothing; simply call inside a component.
 *
 * - Generates the noise buffer once, then reuses it.
 * - Starts instantly on a user gesture (the “Next” click).
 * - Grace-fully no-ops if autoplay with sound is blocked.
 */
export default function useWhiteNoise(enabled, volume = 0.20) {
  const ctxRef = useRef(null);
  const bufferRef = useRef(null);
  const sourceRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      // Fade-out & stop if currently playing
      const node = sourceRef.current;
      if (node && ctxRef.current) {
        node.gain.gain.exponentialRampToValueAtTime(
          0.0001,
          ctxRef.current.currentTime + 0.25
        );
        node.src.stop(ctxRef.current.currentTime + 0.26);
        sourceRef.current = null;
      }
      return;
    }

    // Lazily create (or resume) the AudioContext
    const ctx =
      ctxRef.current ||
      new (window.AudioContext || window.webkitAudioContext)();
    ctxRef.current = ctx;
    if (ctx.state === 'suspended') ctx.resume().catch(() => { });

    // Build a 2-second mono buffer of random samples (once)
    if (!bufferRef.current) {
      const frames = 2 * ctx.sampleRate;
      const buf = ctx.createBuffer(1, frames, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;
      bufferRef.current = buf;
    }

    // Start a looping source
    const src = ctx.createBufferSource();
    src.buffer = bufferRef.current;
    src.loop = true;

    const gain = ctx.createGain();
    gain.gain.value = volume;

    src.connect(gain).connect(ctx.destination);
    src.start();

    sourceRef.current = { src, gain };

    return () => {
      // Clean-up on unmount
      if (sourceRef.current) {
        sourceRef.current.src.stop();
        sourceRef.current = null;
      }
    };
  }, [enabled, volume]);
}